import React, { useContext, useEffect, useMemo, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import useLabels from "../../../../../customHooks/useLabels";
import {
  GetJunctionsData,
  GetJunctionsVars,
  JunctionEdge,
} from "../../masters/subsidary/junctions/queries/types";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../utils/constants";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import { AppContext } from "../../../../../context/context";
import { useLazyQuery } from "@apollo/client";
import { GetJunctions } from "../../masters/subsidary/junctions/queries/queries";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { AtsSubsidiaryOrderField } from "../../masters/subsidary/queries/types";
import { TableHeaderProps } from "../../../../../utils/types";
import { GridAlignment, GridColDef } from "@mui/x-data-grid-pro";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { Checkbox } from "@mui/material";

interface Props {
  selectedIds: number[];
  handleCheckboxChange: (id: number) => void;
}

const JunctionsList = ({ selectedIds, handleCheckboxChange }: Props) => {
  const { junctionLabel } = useLabels();

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [junctions, setJunctions] = useState<JunctionEdge[]>([]);

  
  const { customer_id, sub_id, siteID, company_id } = useUrlParams();
  const { UserRightsConfig } = useAssetTableJson();
  const { state, dispatch } = useContext(AppContext);

  const [getJunctions, { data, loading, fetchMore }] = useLazyQuery<
    GetJunctionsData,
    GetJunctionsVars
  >(GetJunctions);

  useEffect(() => {
    if (
          
      customer_id &&
      state.companyId &&
      state.subsidiaryId &&
      state.subSiteId
    ) {
      getJunctions({
        variables: {
          
          after: null,
          customerID: customer_id,
          companyID: state.companyId,
          first: ROWS_PER_PAGE,
          empID: 0,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsSubsidiaryOrderField.NAME,
          },
          name: EMPTY_STRING,
          subsidiaryID: state.subsidiaryId ? state.subsidiaryId : 0,
          siteID: state.subSiteId ? state.subSiteId : 0,
          ldgrID: 0,
        },
      });
    }
  }, [
    getJunctions,
    customer_id,
    state.companyId,
    state.subSiteId,
    state.subsidiaryId,
    siteID,
  ]);

  const dynamicHeaders: TableHeaderProps[] = UserRightsConfig.List.map(
    (header) => ({
      headerName: header.headerName,
      className: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "checkbox") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedIds.includes(params.row.junctionID)}
                onChange={() => handleCheckboxChange(params.row.junctionID)}
              />
            </>
          );
        }

        return params.value;
      },
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetJunctions.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = junctions.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setJunctions(updatedNewData);
      } else {
        setJunctions(newData);
      }
      setEndCursor(data.GetJunctions.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  const rows = useMemo(() => {
    return data
      ? data.GetJunctions.edges.map(({ node }, index) => ({
          id: index + 1,
          name: node.name,
          junction_name: node.name,
          code: node.code,
          company_addr: node.address,
          junctionID: node.id,
        }))
      : [];
  }, [data]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetJunctions.edges;
              const pageInfo = fetchMoreResult.GetJunctions.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetJunctions.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetJunctions: {
                  edges: [...junctions, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetJunctions.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length) {
      scrollTable.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  return (
    <>
      <Title>{junctionLabel}</Title>
      <StyledDatagrid
        columns={columns}
        rows={rows}
        rowHeight={TABLE_ROW_HEIGHT}
        disableRowSelectionOnClick
        hideFooter
      />
    </>
  );
};

export default JunctionsList;
